/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, February 2020
 */
export type Values = Partial<{ [key: string]: string }>;

/**
 * The types of values returned by the environment.
 */
export type EnvironmentValue = string | number | boolean | EnvironmentValue[];

/**
 * A utility to access environment variables.
 * This parses strings into primitives and also
 */
export class Environment {
    private cache = new Map();

    private readonly NUMBER_PATTERN = /^\d+(\.\d+)?$/;

    private readonly LIST_PATTERN = /^[^,]+,([^,]+,?)*$/;

    private readonly BOOLEAN_PATTERN = /^(true|false)$/;

    constructor(private readonly values: Values) {}

    /**
     * Get a value from the environment.
     * This could be from the Node.js built-in `process.env`, or the `process.env` defined by Webpack's `EnvironmentPlugin`.
     *
     * @param name Name of the variable.
     * @param defaultValue A value to return if the variable is not present.
     * @returns Value from the environment parsed as a native data type.
     * @throws {Error} When the given name is not present in the environment.
     * @typeparam T The type of variable you retrieve. It must be a {@link EnvironmentValue}
     */
    public get<T extends EnvironmentValue>(name: string, defaultValue?: T): T {
        const value = this.values[name];

        if (value === undefined) {
            if (defaultValue !== undefined) return defaultValue;
            throw new Error(`key \`${name}\` is not set`);
        }

        if (this.cache.has(name)) return this.cache.get(name);

        const parsed = this.parse(value);
        this.cache.set(name, parsed);
        return parsed as any; // eslint-disable-line @typescript-eslint/no-explicit-any
    }

    /**
     * Whenever you `get` a value it gets stored in a cache to avoid parsing it multiple times.
     * This method will remove all entries from the cache.
     * This is useful mainly for testing but it can be useful in other scenarios (e.g. like dynamically changing environment variables in Node.js).
     */
    public clean(): void {
        this.cache.clear();
    }

    private parse(value: string): EnvironmentValue {
        if (this.NUMBER_PATTERN.test(value)) {
            return parseFloat(value);
        }
        if (this.LIST_PATTERN.test(value)) {
            const values = value.split(',');
            return values.filter(v => v != null && v !== '').map(v => this.parse(v));
        }
        if (this.BOOLEAN_PATTERN.test(value)) {
            return value === 'true';
        }
        return value.replace(/^("|')|("|')$/g, '').trim();
    }
}

/**
 * The environment instance.
 */
export const environment = new Environment(process.env);



// WEBPACK FOOTER //
// ./src/packages/core/lib/config/Environment.ts